import React, { useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Button, Dropdown, Tooltip, Typography } from 'antd';

import ContentOptimizerIcon from '../../../resources/icons/ContentOptimizer';
import ChatGPTIcon from '../../../resources/icons/editor/ChatGPTIcon';
import SiderChevronDownIcon from '../../../resources/icons/SiderChevronDown';
import SiderChevronUpIcon from '../../../resources/icons/SiderChevronUp';
import ContentOutlineIcon from '../../../resources/icons/viewOnlyNavMenu/ContentOutlineIcon';
import { colorPalette } from '../../../resources/styles/colorPalette';

import { axiosAPI } from '../../../utils/axiosAPI';
import { isCurrentAiProcessInProgress } from '../../../utils/helpers/aiOptimizerHelpers';
import { isCurrentOutlineExists } from '../../../utils/helpers/contentOptimizerHelpers';
import { getAxiosHeaders } from '../../../utils/helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../../utils/helpers/openBasicErrorNotification';
import { getThemeColorClass, getTooltipOverlayClass } from '../../../utils/helpers/themeHelpers';
import { useIconColor } from '../../../utils/hooks/useIconColor';
import { useOpen } from '../../../utils/hooks/useOpen';
import { validateHeadingRequirements } from '../tiptapEditor/utils/headingCountHelpers';
import { refactorEditorVersion } from '../tiptapEditor/utils/versionHelpers';

import { aiInstanceTypes, aiOutlineOptions } from '../../../constants/aiOptimizerConstants';
import { contentOptimizerBriefSubTabKey } from '../../../constants/contentOptimizer';
import { contentBriefPathes } from '../../../constants/queryPathes';
import { subscriptionLimits as subscriptionLimitsNames } from '../../../constants/subscriptionLimits';
import { aiContentFields } from '../../pages/contentOptimizer/contentPage/content/briefTab/contentBriefsConstants';
import { briefOutlineTabs } from '../../pages/contentOptimizer/contentPage/contentOptimizerContentConstants';
import { editorTypes } from '../tiptapEditor/constants/tiptapEditorConstants';

import useEditorAiCredits from '../tiptapEditor/hooks/useEditorAiCredits';
import NoCreditsMessage from '../tiptapEditor/toolbar/buttons/content/NoCreditsMessage';

import { setRankabilityLoading } from '../../../userBrowserSettings/store/browserSettings.actions';
import { selectDarkMode } from '../../../userBrowserSettings/store/browserSettings.selectors';
import { increaseSubscriptionLimits } from '../../pages/account/billing/store/billingSettings.actions';
import {
    setAIBriefLoading,
    setAIContentLoadingField,
    setBriefVersions,
} from '../../pages/contentOptimizer/contentPage/content/briefTab/content/tabsContent/store/contentBriefsOutline.actions';
import {
    selectAIBriefLoading,
    selectAIContentLoading,
    selectBriefVersions,
    selectCurrentBrief,
} from '../../pages/contentOptimizer/contentPage/content/briefTab/content/tabsContent/store/contentBriefsOutline.selectors';
import {
    updateCurrentEditorVersion,
    updateEditorVersions,
} from '../tiptapEditor/store/tiptapEditor.actions';
import {
    selectCurrentEditorVersion,
    selectEditorVersions,
} from '../tiptapEditor/store/tiptapEditor.selectors';

import ImportAiContentModal from '../createNew/ImportAiContentModal';
import EditAIOptimizerOptions from './EditAIOptimizerOptions';

import './AIButton.scss';

const classes = {
    AIButtonWrapper: 'ai-button',
    AIButtonLoading: 'ai-button-loading',

    dropdownOverlay:
        'gray-dropdown-overlay dropdown-with-arrow-overlay basic-more-dropdown-overlay dropdown-menu dropdown-overlay-with-extra-content',
    dropdownOverlayDark:
        'dropdown-with-arrow-overlay-dark basic-more-dropdown-overlay-dark dropdown-overlay-with-extra-content-dark',

    tooltipOverlay: 'toolbar-tooltip-overlay',
    aiOptionLabel: 'ai-option-label',
};

const editorType = editorTypes.OUTLINE;

const AIButton = () => {
    const { contentOptimizerId } = useParams();

    const dispatch = useDispatch();

    const darkMode = useSelector(selectDarkMode);
    const basicIconColor = useIconColor();

    const outlineVersions = useSelector(selectEditorVersions(editorType));
    const activeOutlineVersion = useSelector(selectCurrentEditorVersion(editorType));

    const briefVersions = useSelector(selectBriefVersions);
    const activeBriefVersion = useSelector(selectCurrentBrief);

    const isAiContentLoading = useSelector(selectAIContentLoading);
    const isBriefOrOutlineLoading = useSelector(selectAIBriefLoading);

    const { hasSufficientAiCredits, aiCreditsResetDate, redirectToUpgrade } = useEditorAiCredits();

    const [importedContentData, setImportedContentData] = useState('');

    const [isAIOptimizerOptionsOpen, handleAIOptimizerOptionsOpen] = useState(false);
    const [isAICreateContentSelected, handleAICreateContentSelected] = useState(false);

    const { isOpen: isDropdownOpen, handleOpen: handleDropdownOpen } = useOpen();
    const { isOpen: isImportModalOpen, handleOpen: handleImportModalOpen } = useOpen();

    const isOutlineTab =
        localStorage.getItem(contentOptimizerBriefSubTabKey) === briefOutlineTabs.outline.key;

    const aiLoading = useMemo(() => {
        if (isAiContentLoading) return true;

        if (isOutlineTab) {
            return (
                !!activeOutlineVersion &&
                isCurrentAiProcessInProgress(activeOutlineVersion.generation_status)
            );
        }

        return (
            !!activeBriefVersion &&
            isCurrentAiProcessInProgress(activeBriefVersion.generation_status)
        );
    }, [isOutlineTab, activeOutlineVersion, isAiContentLoading, activeBriefVersion]);

    const notEnoughAICredits = isOutlineTab
        ? !hasSufficientAiCredits.outline && !hasSufficientAiCredits.content
        : !hasSufficientAiCredits.brief;

    const headingValidation = validateHeadingRequirements(activeOutlineVersion?.heading_count);
    const isAiContentButtonDisabled =
        !isCurrentOutlineExists(activeOutlineVersion?.outline) || !headingValidation.isValid;

    const themeColorClass = getThemeColorClass(darkMode);
    const dropdownIconColor =
        aiLoading || notEnoughAICredits ? colorPalette.colorBadgeGray : basicIconColor;
    const aiButtonIconColor = darkMode
        ? colorPalette.iconColor.dark
        : colorPalette.textAccentColor.light;

    const commonTooltipProps = {
        overlayClassName: `${classes.tooltipOverlay} ${getTooltipOverlayClass(darkMode)}`,
        placement: 'top',
    };

    const generateAIBriefOrOutline = (briefOptions = null) => {
        if (isBriefOrOutlineLoading) {
            return;
        }

        handleAIOptimizerOptionsOpen(false);
        dispatch(setAIBriefLoading(true)); // means AI Brief or Outline

        axiosAPI
            .post(
                `${contentBriefPathes.generateOutline}/${contentOptimizerId}/generate`,
                {
                    options: {
                        brief: !isOutlineTab,
                        outline: isOutlineTab,
                        fields_to_update: briefOptions,
                    },
                },
                { ...getAxiosHeaders() }
            )
            .then((result) => {
                const updatedOutlineVersions =
                    outlineVersions?.length > 0
                        ? [
                              ...outlineVersions?.map((version) => ({
                                  ...version,
                                  active: false,
                              })),
                              refactorEditorVersion(result.data?.outline, editorType),
                          ]
                        : [result.data?.outline];

                const updatedBriefVersions =
                    briefVersions?.length > 0
                        ? [
                              ...briefVersions.map((version) => ({
                                  ...version,
                                  active: false,
                              })),
                              result.data?.version,
                          ]
                        : [result.data?.version];

                if (result.data?.openai_credits_charged > 0) {
                    dispatch(
                        increaseSubscriptionLimits({
                            limit: subscriptionLimitsNames.openaiBriefsUsed,
                            value: result.data.openai_credits_charged,
                        })
                    );
                }

                batch(() => {
                    dispatch(
                        updateEditorVersions({ editorType, versions: updatedOutlineVersions })
                    );
                    dispatch(
                        updateCurrentEditorVersion({ editorType, version: result.data?.outline })
                    );
                    dispatch(setBriefVersions(updatedBriefVersions));
                });
            })
            .catch(({ response }) => {
                openBasicErrorNotification(response?.data?.message);
            })
            .finally(() => {
                dispatch(setAIBriefLoading(false));
            });
    };

    const handleAIButtonClick = () => {
        handleAICreateContentSelected(false);
        handleAIOptimizerOptionsOpen(true);
    };

    const handleCancelAIButtonClick = () => {
        handleAIOptimizerOptionsOpen(false);
    };

    const handleAIContentButtonClick = () => {
        handleAICreateContentSelected(true);
        handleAIOptimizerOptionsOpen(true);
    };

    const generateAIContent = (briefOptions = null) => {
        if (!isOutlineTab) return;

        handleAIOptimizerOptionsOpen(false);
        dispatch(setAIContentLoadingField(aiContentFields.OPTIMIZER));

        axiosAPI
            .post(
                `${contentBriefPathes.generateOutline}/${contentOptimizerId}/ai-content/generate`,
                {
                    fields_to_update: briefOptions,
                },
                { ...getAxiosHeaders() }
            )
            .then(({ data }) => {
                if (data?.openai_credits_cost) {
                    dispatch(
                        increaseSubscriptionLimits({
                            limit: subscriptionLimitsNames.openaiBriefsUsed,
                            value: data?.openai_credits_cost,
                        })
                    );
                } else {
                    openBasicErrorNotification();
                    batch(() => {
                        dispatch(setRankabilityLoading(0));
                        dispatch(setAIContentLoadingField(null));
                    });
                }
            })
            .catch(({ response }) => {
                openBasicErrorNotification(response?.data?.message);
                batch(() => {
                    dispatch(setRankabilityLoading(0));
                    dispatch(setAIContentLoadingField(null));
                });
            });
    };

    const resetImportedContentData = () => {
        setImportedContentData('');
    };

    const getNoCreditsTooltipTitle = (isShown = false) => {
        return isShown ? (
            <NoCreditsMessage
                creditsResetDate={aiCreditsResetDate}
                redirectToUpgrade={redirectToUpgrade}
                creditsType='AI credits'
            />
        ) : null;
    };

    const aiOutlineDropdownItems = [
        {
            key: 'new-ai-outline',
            label: (
                <Tooltip
                    {...commonTooltipProps}
                    title={getNoCreditsTooltipTitle(!hasSufficientAiCredits.outline)}
                >
                    <div
                        className={`${classes.aiOptionLabel} ${
                            !hasSufficientAiCredits.outline ? 'disabled' : ''
                        }`}
                    >
                        <ContentOutlineIcon color={dropdownIconColor} />
                        <span>{aiOutlineOptions.newOutline.label}</span>
                        <span>{aiOutlineOptions.newOutline.credits}</span>
                    </div>
                </Tooltip>
            ),
            onClick: () => {
                handleAIButtonClick();
                handleDropdownOpen(false);
            },
            disabled: !hasSufficientAiCredits.outline,
        },
    ];

    if (JSON.parse(process.env.REACT_APP_SHOW_GENERATE_AI_CONTENT_BUTTON ?? 'false')) {
        aiOutlineDropdownItems.push({
            key: 'generate-ai-content-from-outline',
            label: (
                <Tooltip
                    {...commonTooltipProps}
                    title={
                        getNoCreditsTooltipTitle(!hasSufficientAiCredits.content) ||
                        headingValidation.message
                    }
                >
                    <div
                        className={`${classes.aiOptionLabel} ${
                            !hasSufficientAiCredits.content ? 'disabled' : ''
                        }`}
                    >
                        <ContentOptimizerIcon color={dropdownIconColor} />
                        <span>{aiOutlineOptions.generateContent.label}</span>
                        <span>{aiOutlineOptions.generateContent.credits}</span>
                    </div>
                </Tooltip>
            ),
            onClick: () => {
                handleAIContentButtonClick();
                handleDropdownOpen(false);
            },
            disabled: !hasSufficientAiCredits.content || isAiContentButtonDisabled,
        });
    }

    const getAiMenuItems = (isOutline = false, isLoading = false, isDisabled = false) => {
        const iconColor = isDisabled ? colorPalette.basicGray : aiButtonIconColor;
        const dropdownIcon = isDropdownOpen ? (
            <SiderChevronUpIcon color={iconColor} />
        ) : (
            <SiderChevronDownIcon color={iconColor} />
        );

        const aiButtonContent = isOutline ? (
            <div className='d-flex align-items-center'>
                <Typography className={`mr-4px ${themeColorClass}`}>AI Outline</Typography>
                {dropdownIcon}
            </div>
        ) : (
            <Typography className={themeColorClass}>AI Brief</Typography>
        );

        const aiButtonElement = (
            <Button
                className={`${classes.AIButtonWrapper} ${isLoading ? classes.AIButtonLoading : ''}`}
                icon={<ChatGPTIcon color={iconColor} />}
                disabled={isDisabled}
                onClick={isOutline ? handleDropdownOpen : handleAIButtonClick}
            >
                <Tooltip
                    {...commonTooltipProps}
                    title={getNoCreditsTooltipTitle(notEnoughAICredits)}
                >
                    {aiButtonContent}
                </Tooltip>
            </Button>
        );

        if (isOutline) {
            return (
                <Dropdown
                    menu={{ items: aiOutlineDropdownItems }}
                    placement='bottom'
                    arrow
                    trigger={['click']}
                    disabled={isDisabled}
                    overlayClassName={`${classes.dropdownOverlay} ${
                        darkMode ? classes.dropdownOverlayDark : ''
                    }`}
                    open={isDropdownOpen}
                    onOpenChange={handleDropdownOpen}
                >
                    {aiButtonElement}
                </Dropdown>
            );
        }

        return aiButtonElement;
    };

    return (
        <div className={`${classes.AIButtonWrapper} ${themeColorClass}`}>
            {getAiMenuItems(isOutlineTab, aiLoading, aiLoading || notEnoughAICredits)}
            <ImportAiContentModal
                isOpen={isImportModalOpen}
                handleOpen={handleImportModalOpen}
                contentData={importedContentData}
                onSubmit={resetImportedContentData}
            />
            {(activeBriefVersion || activeOutlineVersion) && (
                <EditAIOptimizerOptions
                    keywordData={
                        (isOutlineTab ? activeOutlineVersion : activeBriefVersion)?.content_brief
                    }
                    onAIBriefOutlineEvent={
                        isAICreateContentSelected ? generateAIContent : generateAIBriefOrOutline
                    }
                    isOptionModalOpen={isAIOptimizerOptionsOpen}
                    handleOptionModalOpen={handleCancelAIButtonClick}
                    typeSelected={
                        isOutlineTab
                            ? isAICreateContentSelected
                                ? aiInstanceTypes.CONTENT
                                : aiInstanceTypes.OUTLINE
                            : aiInstanceTypes.BRIEF
                    }
                />
            )}
        </div>
    );
};

export default AIButton;
